import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useSettings } from '../../context/SettingsContext';
import { Save, Loader2, Bot, Palette } from 'lucide-react';

const FIELD_META = {
  site_title: { label: 'Sayt başlığı (loqotip mətni)', type: 'text', hint: 'Ana səhifədə və nüməyə görsənən əsas ad', group: 'brand' },
  page_title: { label: 'Brauzer tab-ı başlığı', type: 'text', hint: 'Məs: Dr. Cəbrayıl Abbasov', group: 'brand' },
  brand_name: { label: 'Brend / şəxs adı', type: 'text', hint: 'Footer və s.-də görünməsi üçün', group: 'brand' },
  chat_title: { label: 'Chat penərəsi başlığı', type: 'text', group: 'brand' },
  chat_welcome: { label: 'Chat qarşılama mətni', type: 'textarea', group: 'brand' },
  footer_text: { label: 'Footer mətni', type: 'text', group: 'brand' },
  hero_person_image: { label: 'Ana səhifə şəxs şəkli (URL)', type: 'text', group: 'brand' },

  ai_enabled: { label: 'AI cavabları aktiv olsun', type: 'select', group: 'ai',
    options: [{ value: 'true', label: 'Bəli' }, { value: 'false', label: 'Xeyr' }],
    hint: 'Deaktiv olduqda AI cavab verməyəcək, yalnız admin cavab yaza biləcək' },
  ai_provider: { label: 'AI provayder', type: 'select', group: 'ai',
    options: [
      { value: 'gemini', label: 'Google Gemini' },
      { value: 'openai', label: 'OpenAI GPT' },
    ]},
  ai_model: { label: 'AI Model', type: 'select', group: 'ai',
    options: [
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (sürətli, tovsiyə olunur)' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (daha dəqiq)' },
      { value: 'gpt-5', label: 'GPT-5' },
      { value: 'gpt-5-mini', label: 'GPT-5 mini (əlçatan)' },
      { value: 'gpt-4o', label: 'GPT-4o' },
    ],
    hint: 'Seçilmiş model provayderə uyğun olmalıdır' },
  ai_system_prompt: { label: 'AI-ın təlimatı (System Prompt)', type: 'textarea', group: 'ai',
    hint: 'AI-a necə davranmasını buradan bildirin. Sayt məzmunu (xidmətlər, həkimlər və s.) avtomatik olaraq əlavə edilir.' },
};

const GROUPS = [
  { key: 'brand', title: 'Brend / Mətnlər', icon: Palette,
    fields: ['site_title', 'page_title', 'brand_name', 'footer_text', 'chat_title', 'chat_welcome', 'hero_person_image'] },
  { key: 'ai', title: 'AI köməkçisi', icon: Bot,
    fields: ['ai_enabled', 'ai_provider', 'ai_model', 'ai_system_prompt'] },
];

export default function ManageSettings() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const { refresh } = useSettings();

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/settings');
      setValues(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveOne = async (key) => {
    setSavingKey(key);
    try {
      const value = values[key] ?? '';
      console.log(`Saving setting ${key} with value:`, value);
      const response = await api.put(`/admin/settings/${key}`, { value });
      console.log('Setting saved response:', response.data);
      setSavedKey(key);
      setTimeout(() => setSavedKey(''), 1800);
      // Refresh settings in the context to update the frontend
      console.log('Refreshing settings after save...');
      await refresh();
      console.log('Settings refreshed');
    } catch (error) {
      console.error('Failed to save setting:', key, error);
      alert(`Xəta: ${error.response?.data?.detail || 'Yadda saxlanılmadı'}`);
    } finally { setSavingKey(''); }
  };

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const inputStyle = { flex: 1, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit' };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>Ayarlamalar</h2>
      <p style={{ color: '#6b7280', margin: '0 0 22px', fontSize: 14 }}>Saytda görünən mətnləri və AI köməkçisini buradan idarə edin.</p>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="spin" size={22} /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 26, maxWidth: 760 }}>
          {GROUPS.map((g) => (
            <div key={g.key}>
              <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                <g.icon size={16} color="#17b4b4" />{g.title}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {g.fields.map((k) => {
                  const meta = FIELD_META[k];
                  if (!meta) return null;
                  const value = values[k] ?? '';
                  return (
                    <div key={k} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 4, color: '#111827' }}>{meta.label}</label>
                      {meta.hint && <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>{meta.hint}</p>}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {meta.type === 'textarea' ? (
                          <textarea value={value} onChange={(e) => set(k, e.target.value)} rows={k === 'ai_system_prompt' ? 6 : 3} style={{ ...inputStyle, resize: 'vertical' }} />
                        ) : meta.type === 'select' ? (
                          <select value={value} onChange={(e) => set(k, e.target.value)} style={inputStyle}>
                            {meta.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input type="text" value={value} onChange={(e) => set(k, e.target.value)} style={inputStyle} />
                        )}
                        <button onClick={() => saveOne(k)} disabled={savingKey === k}
                          style={{ background: savedKey === k ? '#10b981' : '#17b4b4', color: '#fff', border: 'none', padding: '0 18px', borderRadius: 8, cursor: savingKey === k ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                          {savingKey === k ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                          {savedKey === k ? 'Yadda saxlandı' : 'Yadda saxla'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
